window.MathUtils = {

	/**
	 * Clamps a numeric value between a minimum and maximum range.
	 * @param {number} value	- The value to clamp
	 * @param {number} [min]	- Minimum allowed value (optional)
	 * @param {number} [max]	- Maximum allowed value (optional)
	 * @returns {number}		- The clamped value
	 */
    Clamp: function (value, min, max) {
        if (isNaN (value)) return min ?? 0;
        if (min != null && value < min) return min;
        if (max != null && value > max) return max;
        return value;
    }

};

window.StringUtils = {

	/**
	 * Replaces multiple patterns in a string, similar to PHP's str_replace
	 * @param {string} subject			- The original string
	 * @param {string|string[]} search	- Pattern(s) to search for
	 * @param {string|string[]} replace	- Replacement(s) corresponding to the patterns
	 * @returns {string}				- The string after replacements
	 */
	StrReplace: function (subject, search, replace) {
		// Convert to array if not already
		const searches = Array.isArray(search) ? search : [search];
		const replaces = Array.isArray(replace) ? replace : [replace];

		return searches.reduce((acc, s, i) => {
			const r = replaces[i] !== undefined ? replaces[i] : "";
			// Replace all occurrences using split/join
			return acc.split(s).join(r);
		}, subject);
	},

	/**
	 * Returns a string combining a count with the correctly pluralized word.
	 * @param {number} count - The quantity to display.
	 * @param {string} singular - The singular form of the word.
	 * @param {string|null} [plural=null] - Optional plural form of the word.
	 *                                       If not provided, 's' is appended to singular.
	 * @returns {string} The count followed by the appropriate singular or plural word.
	 */
	Pluralize: function (count, singular, plural = null) {
		return `${count} ${count === 1 ? singular : (plural || singular + "s")}`;
	},

	/**
	 * Converts a string to a safe filename with optional rules
	 * @param {string} name							- Original string
	 * @param {object} [options]					- Optional rules
	 * @param {boolean} [options.space=true]		- Replace spaces with underscore if true
	 * @param {boolean} [options.diacritics=true]	- Remove accents/diacritics if true
	 * @returns {string}							- Sanitized filename
	 */
	Sanitize: function (name, options = {}) {
		const { space = true, diacritics = true } = options;
		let result 		= name;
		// Normalize accents if needed
		if (diacritics) {
			result		= result.normalize ("NFD").replace (/[\u0300-\u036f]/g, "");
		}
		// Replace unwanted characters
		const pattern 	= space ? /[^a-zA-Z0-9-_.]/g : /[^a-zA-Z0-9-_. ]/g;
    	result = result.replace (pattern, "_");
		if (space) {
			result 		= result.replace(/ /g, "_");
		}
		// Collapse multiple underscores and trim
		result 			= result.replace (/_+/g, "_").replace (/^_|_$/g, "");
		return result;
	},

	_looksLikeMojibake (str) {
		return /(?:Ã.|â.|ð.|ï.|�)/.test (str);
	},

	SafeFixEmoji (str) {
		if (typeof str !== 'string') return str;
		return this._looksLikeMojibake (str)
			? new TextDecoder ('utf-8').decode (
				Uint8Array.from (str, c => c.charCodeAt(0))
			)
			: str;
	}

};

window.Utils = {

	/**
	 * Convert a value to a boolean, handling strings, numbers, and native booleans.
	 *
	 * @param {*} value - The value to convert. Can be a boolean, string, number, or any type.
	 *	- Booleans are returned as-is.
	 *	- Strings "true" or "1" return true; "false" or "0" return false (case-insensitive).
	 *	- Numbers: 0 returns false, any other number returns true.
	 *	- Falsy values (null, undefined, "", NaN) return false; all other truthy values return true.
	 * @returns {boolean} The converted boolean value.
	 */
	ParseBool: function (value) {
		if (typeof value === "boolean")			return value;
		if (typeof value === "string") {
			const v = value.toLowerCase();
			if (v === "true" || v === "1")		return true;
			if (v === "false" || v === "0")		return false;
		}
		if (typeof value === "number")			return value !== 0;
		// fallback for null, undefined, nombres, etc.
		return Boolean (value);
	},

	/**
	 * Handles changes on an <input> element that should accept only integer values.
	 * Ensures the value stays within the input's min/max range.
	 * @param {Event} e - The input event
	 */
    OnIntValueChange: function (e) {
        const textInput = e.target;
		// Get the minimum and maximum values from the input attributes
        const min       = parseInt (textInput.min);
        const max       = parseInt (textInput.max);
        // correct value
		let value       = textInput.value.trim ();
        value           = value.replace (/[^0-9]/g, '');
        value           = parseInt (value);
		// Apply corrected or default value
        textInput.value = (isNaN (value) || value === "")
            ? ""
            : MathUtils.Clamp (value, min, max);
    },

	/**
	 * Handles changes on an <input> element that should accept float values.
	 * Ensures the value:
	 *  - contains only digits and a single decimal point
	 *  - has at most one decimal digit
	 *  - stays within the input's min/max range on 'change' events
	 * @param {Event} e - The input event
	 */
    OnFloatValueChange: function (e) {
		const textInput		= e.target;
		const min			= parseFloat (textInput.min);
		const max			= parseFloat (textInput.max);
		// correct value
		let value			= textInput.value.trim ();
		value				= value.replace(/[^0-9.]/g, '');
		const parts 		= value.split('.');
		if (parts.length > 2) {
			value       	= parts[0] + '.' + parts.slice (1).join ('');
		}
		// limit to 1 decimal
		if (parts.length === 2) {
			parts[1]		= parts[1].substring (0, 1);
			value			= parts[0] + '.' + parts[1];
		}
		// limit to min/max
		if (e.type === 'change') {
			let num			= parseFloat(value);
			num				= MathUtils.Clamp (num, min, max);
			textInput.value = isNaN (num) ? '' : num.toFixed (1);
		} else {
			textInput.value = value;
		}
	},


	//**-- Link Utils --**//

	/**
	 * Downloads a file from a given URL, optionally specifying a download name.
	 * Steps performed:
	 *  1. Validate that a file URL is provided
	 *  2. Determine a download name if not provided (extract from URL or fallback)
	 *  3. Fetch the file as a Blob
	 *  4. Create a temporary link element to trigger the download
	 *  5. Clean up temporary objects
	 * @param {string} fileUrl			- The URL of the file to download
	 * @param {string} [downloadName]	- Optional filename for the downloaded file
	 */
	DownloadFile: async function (fileUrl, downloadName) {
		// Validate file URL
		if (!fileUrl) {
			console.error ("Download File: file URL must be provided");
			return;
		}
		// If no download name provided, attempt to extract it from the URL
		if (!downloadName) {
			try {
				// Try using the URL API
				const urlObj 	= new URL (fileUrl, window.location.href);
				const pathname	= urlObj.pathname;
				downloadName	= pathname.split('/').pop() || "download";
			} catch (e) {
				// Fallback: simple regex/string operations if URL is invalid
				const cleanUrl	= fileUrl.split('?')[0].split('#')[0];
            	downloadName	= cleanUrl.split('/').pop() || "download";
			}
		}
		// Create a temporary link element to trigger the download
		try {
			// Fetch the file as a Blob
			const response	= await fetch (fileUrl, { mode: "cors" });
        	const blob		= await response.blob ();
			const blobUrl	= window.URL.createObjectURL (blob);
			// Create temporary <a> element
			const a			= document.createElement ('a');
			a.href			= blobUrl;
			a.download		= downloadName;
			document.body.appendChild (a);
			// init and trigger download
			a.style.display	= "none";
			document.body.appendChild (a);
			a.click ();
			// clean up
			document.body.removeChild (a);
			window.URL.revokeObjectURL (blobUrl);
		}  catch (err) {
			console.error ("Download failed", err);
		}
	}

};